import React, { ComponentType } from 'react';
import styles from './hoc.module.sass';

const withResponsiveStyles = <P extends object>(WrappedComponent: ComponentType<P>) => {
    return (
        props: P & {
            margin?: ResponsiveValue;
            marginStart?: ResponsiveValue;
            marginEnd?: ResponsiveValue;
            marginTop?: ResponsiveValue;
            marginBottom?: ResponsiveValue;
            padding?: ResponsiveValue;
            paddingStart?: ResponsiveValue;
            paddingEnd?: ResponsiveValue;
            paddingTop?: ResponsiveValue;
            paddingBottom?: ResponsiveValue;
            size?: ResponsiveSize;
            placement?: ResponsivePlacement;
            order?: ResponsiveOrder;
            direction?: ResponsiveDirection;
            gap?: ResponsiveValue;
            theme?: ResponsiveTheme;
            itemCount?: ResponsiveItemCount;
            width?: ResponsiveValue;
            height?: ResponsiveValue;
            border?: ResponsiveBorder;
            className?: string;
        }
    ) => {
        const {
            className,
            margin,
            marginStart,
            marginEnd,
            marginTop,
            marginBottom,
            padding,
            paddingStart,
            paddingEnd,
            paddingTop,
            paddingBottom,
            size,
            placement,
            order,
            direction,
            gap,
            theme,
            itemCount,
            width,
            height,
            border,
            ...restProps
        } = props as any;

        // Generate class names based on responsive props
        const responsiveClassNames = generateResponsiveClasses({
            margin,
            marginStart,
            marginEnd,
            marginTop,
            marginBottom,
            padding,
            size,
            paddingStart,
            paddingEnd,
            paddingTop,
            paddingBottom,
            placement,
            order,
            direction,
            gap,
            theme,
            itemCount,
            width,
            height,
            border,
        });

        const combinedClassName = [className, responsiveClassNames].filter(Boolean).join(' ');

        return <WrappedComponent className={combinedClassName} {...(restProps as P)} />;
    };
};

export default withResponsiveStyles;

// Helper function to generate class names
const generateResponsiveClasses = (responsiveValues: {
    margin?: ResponsiveValue;
    marginStart?: ResponsiveValue;
    marginEnd?: ResponsiveValue;
    marginTop?: ResponsiveValue;
    marginBottom?: ResponsiveValue;
    padding?: ResponsiveValue;
    paddingStart?: ResponsiveValue;
    paddingEnd?: ResponsiveValue;
    paddingTop?: ResponsiveValue;
    paddingBottom?: ResponsiveValue;
    size?: ResponsiveSize;
    placement?: ResponsivePlacement;
    order?: ResponsiveOrder;
    direction?: ResponsiveDirection;
    gap?: ResponsiveValue;
    theme?: ResponsiveTheme;
    itemCount?: ResponsiveItemCount;
    width?: ResponsiveValue;
    height?: ResponsiveValue;
    border?: ResponsiveBorder;
}): string => {
    let classes = '';

    // Process each responsive property (margin, size, etc.)
    Object.entries(responsiveValues).forEach(([key, value]) => {
        if (typeof value === 'string' || typeof value === 'number') {
            // Handle simple string values
            classes += ` ${styles[`${key}-${value}`] || ''}`;
        } else if (typeof value === 'object') {
            // Handle responsive object values
            Object.entries(value).forEach(([breakpoint, breakpointValue]) => {
                const className =
                    breakpoint === 'default' ? `${key}-${breakpointValue}` : `${key}-${breakpoint}-${breakpointValue}`;
                classes += ` ${styles[className] || ''}`;
            });
        }
    });

    return classes.trim();
};

type SizeValue =
    | '14'
    | '16'
    | '18'
    | '20'
    | '22'
    | '24'
    | '26'
    | '28'
    | '30'
    | '32'
    | '34'
    | '36'
    | '38'
    | '40'
    | '42'
    | '44'
    | '46'
    | '48';

type StepValue =
    | 'auto'
    | '0'
    | `${
          | 1
          | 2
          | 3
          | 4
          | 5
          | 6
          | 7
          | 8
          | 9
          | 10
          | 11
          | 12
          | 13
          | 14
          | 15
          | 16
          | 17
          | 18
          | 19
          | 20
          | 21
          | 22
          | 23
          | 24
          | 25
          | 26
          | 27
          | 28
          | 29
          | 30
          | 31
          | 32
          | 33
          | 34
          | 35
          | 36
          | 37
          | 38
          | 39
          | 40
          | 41
          | 42
          | 43
          | 44
          | 45
          | 46
          | 47
          | 48
          | 49
          | 50
          | 51
          | 52
          | 53
          | 54
          | 55
          | 56
          | 57
          | 58
          | 59
          | 60
          | 61
          | 62
          | 63
          | 64
          | 65
          | 66
          | 67
          | 68
          | 69
          | 70
          | 71
          | 72}x`;

type Breakpoint = 'default' | 'md' | 'lg';
type Placement = 'none' | 'center' | 'start' | 'end';
type Order = number;
type Direction = 'column' | 'row';
type Theme = 'dark' | 'light' | 'bg' | 'none' | 'brand';
type ItemCount = number;

type BorderType = 'thin' | 'thick' | 'none';

export type ResponsiveValue = StepValue | { [key in Breakpoint]?: StepValue };
type ResponsiveSize = SizeValue | { [key in Breakpoint]?: SizeValue };

export type ResponsivePlacement = Placement | { [key in Breakpoint]?: Placement };

export type ResponsiveOrder = Order | { [key in Breakpoint]?: Order };
export type ResponsiveDirection = Direction | { [key in Breakpoint]?: Direction };
export type ResponsiveTheme = Theme | { [key in Breakpoint]?: Theme };
export type ResponsiveItemCount = ItemCount | { [key in Breakpoint]?: ItemCount };

export type ResponsiveBorder = BorderType | { [key in Breakpoint]?: BorderType };

// export type ResponsiveWidth = SizeValue | { [key in Breakpoint]?: SizeValue };
